import { useTranslation } from 'react-i18next';
import type { UseFormReturn, UseFieldArrayReturn } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { Input } from '@/components/atoms/input';
import { Button } from '@/components/atoms/button';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
} from '@/components/molecules/form-field';
import { PriceConverter } from '@/components/molecules/price-converter';
import type { CurrencyType } from '@/lib/utils/price.utils';
import type { ShippingRateFormData } from '../types/shipping-form.types';

interface ShippingRatePriceRangesProps {
  form: UseFormReturn<ShippingRateFormData>;
  fieldArray: UseFieldArrayReturn<ShippingRateFormData, 'priceRanges'>;
}

export function ShippingRatePriceRanges({ form, fieldArray }: ShippingRatePriceRangesProps) {
  const { t } = useTranslation();
  const { fields, append, remove } = fieldArray;
  const watchCurrency = form.watch('currency');

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('shipping.priceConditions', { defaultValue: 'Price Conditions' })}</CardTitle>
        <CardDescription>
          {t('shipping.priceConditionsDesc', { defaultValue: 'Define order value ranges and the shipping price for each.' })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {fields.map((field, index) => (
          <div key={field.id} className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 items-end p-3 border rounded-lg">
            <FormField
              control={form.control}
              name={`priceRanges.${index}.minPrice`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">{t('shipping.minPrice', { defaultValue: 'Min' })}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`priceRanges.${index}.maxPrice`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">{t('shipping.maxPrice', { defaultValue: 'Max' })}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={`priceRanges.${index}.rate`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs">{t('shipping.rate', { defaultValue: 'Rate' })}</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      {...field}
                      onChange={(e) => field.onChange(e.target.valueAsNumber || 0)}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => remove(index)}
              disabled={fields.length === 1}
              className="shrink-0"
            >
              <Trash2 className="size-4 text-destructive" />
            </Button>

            {(form.watch(`priceRanges.${index}.rate`) ?? 0) > 0 && watchCurrency && (
              <div className="col-span-2 sm:col-span-4">
                <PriceConverter
                  price={form.watch(`priceRanges.${index}.rate`) ?? 0}
                  sourceCurrency={watchCurrency as CurrencyType}
                  compact
                />
              </div>
            )}
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          onClick={() => append({ minPrice: 0, maxPrice: 0, rate: 0 })}
          className="w-full"
        >
          <Plus className="size-4 mr-2" />
          {t('shipping.addRange', { defaultValue: 'Add Range' })}
        </Button>
      </CardContent>
    </Card>
  );
}
